import { computed, inject, provide } from "vue";
import { EventBus } from "@/lib/event_bus";
import { useStore } from "vuex";

import { fullName } from "@/mixins";
import { CalendarEvent } from "@/typings/calendarEvent";
import { Technician } from "@/typings/technician";

export const injectKey = Symbol();

export function useCalendarEvents(provideState = false) {
  const store = useStore();

  async function fetchTechnicians() {
    await store.dispatch("fetchTechnicians");
  }

  const technicianOptions = computed(() => {
    const technicians: Technician[] = store.state.technicians;

    return technicians.map((tech) => ({
      ...tech,
      label: fullName(tech),
      value: tech.user_id
    }));
  });

  function refreshScheduleBoard() {
    EventBus.$emit("refresh-schedule-board");
  }

  function getEntityType(calendarEvent: CalendarEvent) {
    return calendarEvent.recurring ? "Event series" : "Event";
  }

  async function createCalendarEvent(calendarEvent: CalendarEvent) {
    await store.dispatch("createCalendarEvent", calendarEvent);

    window.notify.success(
      `${getEntityType(calendarEvent)} created successfully.`
    );

    refreshScheduleBoard();
  }

  async function updateCalendarEvent(calendarEvent: CalendarEvent) {
    await store.dispatch("updateCalendarEvent", calendarEvent);

    window.notify.success(
      `${getEntityType(calendarEvent)} updated successfully.`
    );

    refreshScheduleBoard();
  }

  async function deleteCalendarEvent(calendarEvent: CalendarEvent) {
    const message = calendarEvent.recurring
      ? "Canceling this event will cancel all events in the series.\nAre you sure you want to proceed?"
      : "Are you sure you want to delete this event?";

    await store.dispatch("confirmDelete", {
      title: "Confirm Delete",
      message
    });

    await store.dispatch("deleteCalendarEvent", calendarEvent.id);

    window.notify.success(
      `${getEntityType(calendarEvent)} deleted successfully.`
    );

    refreshScheduleBoard();
  }

  const CalendarEventsInterface = {
    fetchTechnicians,
    technicianOptions,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent
  };

  if (provideState) provide(injectKey, CalendarEventsInterface);
  return CalendarEventsInterface;
}

export const injectUseCalendarEvents = () =>
  inject<ReturnType<typeof useCalendarEvents>>(injectKey)!;
